import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import CustomTable from "../../common/CustomTable/CustomTable";
import { navigate } from "@reach/router";
import queryString from "query-string";
import _ from "lodash";
import moment from "moment";
import CaseService from "../../../services/CaseService";
import FilterModal from "./modal";
import {
  getCaseLabelByStatus,
  getErrorMessage,
  numberFormat,
  parseTimeStamp,
} from "../../../helpers/functions";
import { Drawer, useMediaQuery } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import labels from "../../../helpers/labels.json";
import { Footer } from "../../internal/CaseCart/Styles";
import { PrimaryCTAButton } from "../Buttons";
import useLoader from "../../../hooks/useLoader";
import useCartCount from "../../../hooks/useCartCount";
import AlertDialog from "../../common/Alert";
import { DangerBtn } from "../../../styles/component/style";
import CartService from "../../../services/CartService";
import { addCartItems } from "../../internal/CaseCart/functions";

const useStyles = makeStyles({
  root: {
    "& .MuiDrawer-paperAnchorRight": {
      zIndex: 2001,
      boxShadow: "1px 0px 20px 0px rgba(0,0,0,0.2)",
    },
  },
});

async function getCases(query = "", id, caseType, status) {
  try {
    let stats = {};
    const response = await CaseService.getCases(query);
    stats = {
      mediationCount: response?.total,
      negotiationCount: response?.total,
      arbitrationCount: response?.total,
      draftCount: status === "draft" ? response?.total : 0,
      allCount:
        status !== "draft" &&
        status !== "underResolution" &&
        status !== "settled" &&
        status !== "awaitingPayment" &&
        status !== "awaitingRespondent"
          ? response?.total
          : 0,
      activeCount: response?.activeCount,
      underResolution: status === "underResolution" ? response?.total : 0,
      settled: status === "settled" ? response?.total : 0,
      inCartCount: status === "awaitingPayment" ? response?.total : 0,
      awaitingRespondentOnBoardingCount:
        status === "awaitingRespondent" ? response?.total : 0,
    };

    return { ...response, stats };
  } catch (error) {
    throw error;
  }
}

const filterByStatus = (selectedFilter) => {
  let key = "status";
  let value = "";
  switch (selectedFilter) {
    case labels["filters.draft"]:
      value = "draft";
      break;
    case labels["filters.awaiting_payment"]:
      value = "awaitingPayment";
      break;
    case labels["filters.under_resolution"]:
      value = "underResolution";
      break;
    case labels["filters.waiting_for_respondant"]:
      value = "awaitingRespondent";
      break;
    case labels["filters.settled"]:
      value = "settled";
      break;
    default:
      break;
  }
  return { key, value };
};

const initFilters = [
  { label: labels["filters.all"], key: "allCount", value: 0 },
  {
    label: labels["filters.waiting_for_respondant"],
    key: "awaitingRespondentOnBoardingCount",
    value: 0,
  },
  {
    label: labels["filters.under_resolution"],
    key: "underResolution",
    value: 0,
  },
  { label: labels["filters.settled"], key: "settled", value: 0 },
  { label: labels["filters.awaiting_payment"], key: "inCartCount", value: 0 },
  { label: labels["filters.draft"], key: "draftCount", value: 0 },
];

const partyRoles = [
  { label: labels["role.claimant"], value: "claimant" },
  { label: labels["role.respondent"], value: "respondent" },
];

let previousCaseType;

const CasesTable = ({
  partyId = null,
  status = null,
  caseType = null,
  draftSelection = false,
  currentCaseType,
}) => {
  const [state, setState] = useState({});
  const [filters, setFilters] = useState(initFilters);
  const placeholderText = labels["table.caseSearch"];
  const [selectedFilter, setSelectedFilter] = useState(
    status
      ? status === "Under Resolution"
        ? status
        : _.capitalize(status)
      : "All",
  );
  const [advanceFilter, setAdvanceFilter] = useState({
    parties: [],
    role: partyRoles[0],
  });
  const [modal, setModal] = useState();
  const MTRef = useRef();
  const [noParty, setNoParty] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const { setTriggerCount } = useCartCount();
  const [open, setOpen] = useState(false);
  const [dialogData, setDialogData] = useState(false);
  const themes = useTheme();
  const isBigScreen = useMediaQuery(themes.breakpoints.up(640));
  const [page, setPage] = useState(1);
  const [trigger, setTrigger] = useState(false);
  const initialCartAction = {
    type: null,
    cases: [],
  };

  const [cartAction, setCartAction] = useState(initialCartAction);

  useEffect(() => {
    // if (selectedFilter !== "Draft") return;

    const fetchSelectedCases = async () => {
      const query = `?page=1&perPage=50&resolutionKind=${caseType}&selected=true&status=draft`;
      const res = await CaseService.getCases(query);
      const rows = res?.data || [];
      setCartAction(rows.length ? { type: "ADD_SINGLE", cases: rows } : null);
    };

    fetchSelectedCases(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleNavigateToParty(type, id, Case) {
    if (Case?.editFlag && caseType === "arbitration") {
      navigate(`/dashboard/cases/create/${id}?caseType=${caseType}`, {
        state: {
          editFlag: Case?.editFlag,
        },
      });
    } else {
      switch (type) {
        case "draft":
          if (Case.totalClaimValue && caseType === "negotiation") {
            navigate(
              `/dashboard/cases/create/${id}/step-3?caseType=${caseType}`,
            );
          } else if (
            Case.respondentName ||
            Case.respondentMobile ||
            Case.respondentEmail ||
            Case.respondentPanNumber ||
            Case?.respondentParties?.length
          ) {
            navigate(
              `/dashboard/cases/create/${id}/step-2?caseType=${caseType}`,
            );
          } else {
            navigate(`/dashboard/cases/create/${id}?caseType=${caseType}`);
          }
          break;
        default:
          navigate(`/dashboard/cases/${id}?caseType=${caseType}`);
          break;
      }
    }
  }

  function setStats(stats) {
    const mapped = initFilters.map((filter) => {
      let returnData = {
        label: filter.label,
        value: filter.value,
      };
      if (stats[filter.key] >= 0) {
        returnData.value = stats[filter.key];
      }
      return returnData;
    });
    setFilters(mapped);
  }

  const columns = [
    {
      field: "id",
      title: labels["table.case_id"],
      tooltip: "Unique Identifier for the Case across IAMC platform",
      sorting: true,
      headerStyle: {
        textAlign: "left",
        width: "9%",
        minWidth: "75px",
        paddingLeft: 12,
      },
      cellStyle: {
        textAlign: "left",
        paddingLeft: 12,
        paddingRight: 8,
      },
      render: (rowData) => (
        <CaseIdLink
          onClick={() =>
            handleNavigateToParty(rowData?.status, rowData?.id, rowData)
          }
          title={rowData.id}
        >
          {rowData.id}
        </CaseIdLink>
      ),
    },

    {
      field: "caseNo",
      title: "Case Number",
      sorting: false,
      hidden: caseType !== "arbitration",
      headerStyle: {
        textAlign: "left",
        width: "11%",
        minWidth: "115px",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "left",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <ContractNumberContainer title={rowData?.caseNo || "-"}>
          {rowData?.caseNo || "-"}
        </ContractNumberContainer>
      ),
    },

    {
      field: "title",
      title: labels["table.case_title"],
      sorting: true,
      tooltip: "Title of the Case",
      headerStyle: {
        textAlign: "left",
        width: "18%",
        minWidth: "150px",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "left",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <CaseTitleContainer
          editFlag={rowData?.editFlag}
          onClick={() =>
            handleNavigateToParty(rowData?.status, rowData?.id, rowData)
          }
          title={rowData?.title}
        >
          <CaseTitleText editFlag={rowData?.editFlag}>
            {rowData?.title}
          </CaseTitleText>
          {rowData?.editFlag ? <EditBadge>Edit</EditBadge> : null}
        </CaseTitleContainer>
      ),
    },

    {
      field: "claimantParty",
      title: labels["table.party"],
      tooltip: "Parties representing to file the case",
      sorting: false,
      headerStyle: {
        textAlign: "left",
        width: "13%",
        minWidth: "110px",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "left",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <PartyNameContainer
          onClick={() =>
            handleNavigateToParty(rowData?.status, rowData?.id, rowData)
          }
          title={rowData?.claimantParty?.name}
        >
          {rowData?.claimantParty?.name || "—"}
        </PartyNameContainer>
      ),
    },

    {
      field: "role",
      title: labels["table.party_role"],
      tooltip:
        "Your role is either Claimant or Respondent with respect to case",
      sorting: false,
      headerStyle: {
        textAlign: "center",
        width: "11%",
        minWidth: "95px",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "center",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <RoleBadge role={rowData.agentRole}>
          {_.startCase(rowData.agentRole)}
        </RoleBadge>
      ),
    },

    {
      field: "respondentName",
      title: labels["table.counterparty"],
      tooltip: "Party countering your case",
      sorting: false,
      headerStyle: {
        textAlign: "left",
        width: "13%",
        minWidth: "110px",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "left",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => {
        const respondentName =
          rowData?.respondentParty?.name ||
          rowData?.respondentName ||
          (rowData?.respondentParties?.length
            ? rowData?.respondentParties[0]?.name
            : null);

        return (
          <PartyNameContainer
            onClick={() =>
              handleNavigateToParty(rowData?.status, rowData?.id, rowData)
            }
            title={respondentName}
          >
            {respondentName || "—"}
          </PartyNameContainer>
        );
      },
    },

    {
      field: "totalClaimValue",
      title: labels["table.claim_value"],
      tooltip: "Claim Value of the Case",
      sorting: false,
      headerStyle: {
        textAlign: "right",
        width: "12%",
        minWidth: "110px",
        paddingRight: 12,
      },
      cellStyle: {
        textAlign: "right",
        paddingLeft: 8,
        paddingRight: 12,
      },
      render: (rowData) => {
        const claimValue = rowData.totalClaimValue;
        return (
          <ClaimValueContainer
            onClick={() =>
              handleNavigateToParty(rowData?.status, rowData?.id, rowData)
            }
            title={
              claimValue
                ? numberFormat(
                    parseFloat(claimValue).toFixed(2),
                    rowData?.currencyUnit,
                  )
                : "Non Monetary"
            }
          >
            {claimValue
              ? numberFormat(
                  parseFloat(claimValue).toFixed(2),
                  rowData?.currencyUnit,
                )
              : "Non Monetary"}
          </ClaimValueContainer>
        );
      },
    },

    {
      field: "status",
      title: labels["table.status"],
      tooltip: "Status of the Case",
      sorting: false,
      headerStyle: {
        textAlign: "center",
        width: "10%",
        minWidth: "90px",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "center",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <StatusContainer
          onClick={() =>
            handleNavigateToParty(rowData?.status, rowData?.id, rowData)
          }
        >
          {getCaseLabelByStatus(rowData)}
        </StatusContainer>
      ),
    },

    {
      field: "loanAccountNo",
      title: "Contract Number",
      sorting: false,
      hidden: caseType === "arbitration" ? false : true,
      headerStyle: {
        textAlign: "left",
        width: "11%",
        minWidth: "100px",
        paddingLeft: 8,
      },
      cellStyle: {
        textAlign: "left",
        paddingLeft: 8,
        paddingRight: 8,
      },
      render: (rowData) => (
        <ContractNumberContainer
          onClick={() =>
            handleNavigateToParty(rowData?.status, rowData?.id, rowData)
          }
          title={rowData.loanAccountNo}
        >
          {rowData.loanAccountNo || "—"}
        </ContractNumberContainer>
      ),
    },

    {
      field: "created_at",
      title: labels["table.created"],
      tooltip: "Case Created Date",
      sorting: true,
      headerStyle: {
        textAlign: "right",
        width: "11%",
        minWidth: "105px",
        paddingRight: 12,
      },
      cellStyle: {
        textAlign: "right",
        paddingRight: 12,
        paddingLeft: 8,
      },
      render: (rowData) => (
        <DateColumnContainer>
          <DateText
            onClick={() =>
              handleNavigateToParty(rowData?.status, rowData?.id, rowData)
            }
          >
            {rowData?.submittedOn
              ? moment(rowData.submittedOn).format("DD/MM/YYYY")
              : moment(parseTimeStamp(rowData.created_at)).format("DD/MM/YYYY")}
          </DateText>
          {rowData?.status === "draft" && (
            <DeleteButton onClick={() => deleteWaring(false, rowData?.id)}>
              {labels["table.delete"]}
            </DeleteButton>
          )}
        </DateColumnContainer>
      ),
    },
  ];

  /**
   * @description Function to deleted the selected case
   */
  const deleteSelectedDraft = async () => {
    try {
      setLoader({ state: true, message: "Deleting Cases..." });
      const res = await CaseService.deleteSelectedCases();
      if (res?.message) {
        enqueueSnackbar(res?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      MTRef.current.onQueryChange("");
      setOpen(false);
    }
  };

  /**
   * @description Function to trigger the delete case
   * @param {*} param0
   */
  const deleteDraft = async (id) => {
    try {
      setLoader({ state: true, message: "Deleting Cases..." });
      const res = await CaseService.deleteDraftCase(id);
      if (res?.message) {
        enqueueSnackbar(res?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      MTRef.current.onQueryChange("");
      setOpen(false);
    }
  };

  useEffect(() => {
    if (trigger || caseType) {
      MTRef.current.onQueryChange(MTRef?.current?.state?.query);
      setTrigger(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, caseType]);

  const data = (query) =>
    new Promise((resolve) => {
      let params = {
        page: query.page + 1,
        perPage: 10,
      };
      if (previousCaseType !== currentCaseType) {
        params.page = 1;
        setPage(params.page);
      } else {
        setPage(params.page);
      }
      previousCaseType = currentCaseType;
      if (partyId) {
        params.partyId = partyId;
      }
      params.selected = true;
      let partyQueryString = "";
      if (query.search) {
        params.search = query.search;
      }
      if (query.orderBy?.field) {
        params.sort = query.orderBy.field;
        params.sortDirection = query.orderDirection;
      }
      if (caseType) {
        params.resolutionKind = caseType;
      }
      const { key, value } = filterByStatus(selectedFilter);
      if (key && value) {
        params[key] = value;
      }
      if (advanceFilter?.parties?.length > 0) {
        const partyId = advanceFilter?.parties?.map((el) => el.id);
        partyQueryString = queryString.stringify(
          { partyIds: partyId },
          { arrayFormat: "index" },
        );
        params.agentRole = advanceFilter.role.value;
      }
      let stringParams = "";
      if (!_.isEmpty(params)) {
        stringParams = `?${queryString.stringify(params)}`;
      }
      if (partyQueryString) {
        stringParams += `&${partyQueryString}`;
      }
      getCases(stringParams, partyId, caseType, value)
        .then(async (result) => {
          if (result) {
            if (result.message === "Your are not associated with any Party") {
              resolve({
                data: [],
                page: 0,
                total: 0,
              });
              setState({
                data: [],
                lastPage: 1,
                page: 1,
                perPage: 10,
                total: 0,
              });
              setNoParty(true);
            } else {
              const rowData = result?.data?.map((row) => ({
                ...row,
                tableData: { checked: row?.isSelected },
              }));
              resolve({
                data: rowData,
                page: result.page - 1,
                total: result.total,
                lastPage: result.lastPage,
              });
              setState({
                data: rowData,
                page: result.page - 1,
                total: result.total,
                lastPage: result.lastPage,
              });
              setStats(result.stats);
            }
          }
        })
        .catch((error) => {
          enqueueSnackbar(getErrorMessage(error), {
            variant: "error",
          });
          setState({ data: [], lastPage: 1, page: 1, perPage: 10, total: 0 });
          resolve({
            data: [],
            page: 0,
            total: 0,
          });
        });
    });

  const handleCaseSelection = async (cases, data) => {
    const baseQuery = `resolutionKind=${caseType}&status=draft`;
    try {
      setLoader({ state: true, message: "Modifying Cases..." });

      if (!data) {
        const selectedFlag = cases.length > 0 ? "true" : "false";
        const query = `?page=${page}&perPage=50&${baseQuery}&selected=${selectedFlag}`;

        const res = await CaseService.getCases(query); // single call only
        const currentPageCases = res?.data || [];

        if (cases.length > 0) {
          await Promise.all(
            currentPageCases.map((row) => CaseService.selectCaseItem(row.id)),
          );
          setCartAction({ type: "ADD_PAGE", cases: currentPageCases });
        } else {
          await Promise.all(
            currentPageCases.map((row) => CaseService.removeCaseItem(row.id)),
          );
          setCartAction({ type: null, cases: [] });
        }
        return;
      }

      const isChecked = data?.tableData?.checked;
      if (isChecked) {
        await CaseService.selectCaseItem(data.id);
        setCartAction((prev) => ({
          type: "ADD_SINGLE",
          cases: [...(prev?.cases || []), data],
        }));
      } else {
        await CaseService.removeCaseItem(data.id);
        setCartAction((prev) => ({
          type: "ADD_SINGLE",
          cases: prev.cases.filter((c) => c.id !== data.id),
        }));
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
      MTRef.current.onQueryChange("");
    }
  };

  const deleteWaring = (isSelectAll, id) => {
    setOpen(true);
    setDialogData({
      primaryBtnText: "Proceed",
      secondaryBtnText: "Cancel",
      clickPrimaryBtn: () =>
        isSelectAll ? deleteSelectedDraft() : deleteDraft(id),
      clickSecondarybtn: () => setOpen(false),
      onDialogClose: () => setOpen(false),
      desc: `Are you sure you want to delete this draft case(s)?`,
      heading: `Delete (${cartAction?.cases?.length}) Case`,
      descriptionTextStyle: {
        textAlign: "center",
      },
    });
  };

  const submitCaseWarning = () => {
    setOpen(true);
    setDialogData({
      secondaryBtnText: "Cancel",
      primaryBtnText: "Proceed",
      clickPrimaryBtn: () => selectAllSubmit(),
      clickSecondarybtn: () => setOpen(false),
      desc: `Before submitting the case, please check all the information that you have provided. The proceedings shall be conducted based on this information.`,
      heading: `Submit (${cartAction?.cases?.length}) Cases`,
      descriptionTextStyle: {
        textAlign: "center",
      },
    });
  };

  const submitProforma = () => {
    setOpen(true);
    setDialogData({
      secondaryBtnText: "Cancel",
      primaryBtnText: "Proceed",
      clickPrimaryBtn: () => handleProceedGenerateProforma(),
      clickSecondarybtn: () => setOpen(false),
      desc: `Are you sure you want to generate proforma?`,
      heading: `Generate Proforma (${cartAction?.cases?.length}) Cases`,
      descriptionTextStyle: {
        textAlign: "center",
      },
    });
  };

  const handleProceedGenerateProforma = async () => {
    try {
      await executeCartAction();
      await generateProforma();

      // clear table selection
      MTRef.current.onQueryChange("");
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setOpen(false);
    }
  };

  const executeCartAction = async () => {
    if (!cartAction?.cases?.length) {
      enqueueSnackbar("No cases selected", { variant: "warning" });
    }

    try {
      setLoader({ state: true, message: "Updating Cart..." });

      const param = `?resolutionKind=${caseType}`;

      await CaseService.submitDraftCase(param);
      const cart_items = await addCartItems(
        cartAction.cases,
        "caseRegistration",
      );

      setCartAction(initialCartAction);

      return cart_items;
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  async function generateProforma(toChange = true) {
    try {
      setLoader({ state: true, message: "Submitting Cases..." });
      const result = await CartService.generatePerforma();
      if (result?.status === "proformaGenerated") {
        enqueueSnackbar("Profoma invoice is generated Successfully", {
          variant: "success",
        });
        navigate("/dashboard/payments");
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setTriggerCount(true);
      setOpen(false);
      setDialogData({});
      MTRef.current.onQueryChange("");
    }
  }

  const selectAllSubmit = async () => {
    try {
      setLoader({ state: true, message: "Submitting Cases..." });
      const param = `?resolutionKind=${caseType}`;
      const res = await CaseService.submitDraftCase(param);
      if (res?.message) {
        enqueueSnackbar(res?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setTriggerCount(true);
      setOpen(false);
      setDialogData({});
      MTRef.current.onQueryChange("");
    }
  };

  const classes = useStyles();

  return (
    <Container>
      {columns.length > 0 && (
        <CustomTable
          onSelectionChange={handleCaseSelection}
          selection={draftSelection && selectedFilter === "Draft"}
          hidePagination={state.lastPage === 1}
          pageSize={state?.data?.length ? state?.data?.length : 10}
          pluralTitle={labels["cases"]}
          singularTitle={
            noParty
              ? labels["party"].toLowerCase()
              : labels["case"].toLowerCase()
          }
          customFilter={partyId ? false : true}
          onFilterClick={() => setModal(true)}
          filterCount={advanceFilter?.parties?.length}
          customMessage={noParty ? labels["table.caseError"] : null}
          {...{
            columns,
            data,
            filters,
            selectedFilter,
            setSelectedFilter,
            placeholderText,
            MTRef,
            page,
            setPage,
          }}
          state={state}
          left={isBigScreen ? true : false}
          top={isBigScreen ? "140px" : "-50px"}
        />
      )}

      <Footer>
        <ButtonContainer>
          <div
            className="button-row"
            style={{
              display:
                selectedFilter === "Draft" &&
                state?.data?.some((el) => el.tableData?.checked)
                  ? "flex"
                  : "none",
            }}
          >
            <DangerBtn
              style={{
                width: "216px",
                marginLeft: "26px",
                marginTop: 20,
              }}
              onClick={() => deleteWaring(true)}
            >
              {labels["table.delete"]}
            </DangerBtn>

            <PrimaryCTAButton
              style={{
                width: "auto",
                marginLeft: "26px",
                marginTop: 20,
              }}
              onClick={() => submitProforma()}
            >
              Confirm & Generate Proforma
            </PrimaryCTAButton>

            <PrimaryCTAButton
              style={{
                width: "216px",
                marginLeft: "26px",
                marginTop: 20,
              }}
              onClick={() => submitCaseWarning()}
            >
              {labels.proceed}
            </PrimaryCTAButton>
          </div>
        </ButtonContainer>
      </Footer>
      <Drawer
        variant="persistent"
        className={classes.root}
        anchor={"right"}
        open={modal}
        onClose={() => setModal(false)}
      >
        <FilterModal
          {...{
            modal,
            setModal,
            advanceFilter,
            setAdvanceFilter,
            partyRoles,
            setTrigger,
          }}
        />
      </Drawer>
      <AlertDialog isOpen={open} {...{ ...dialogData }} />
    </Container>
  );
};

export default CasesTable;

const ButtonContainer = styled.div`
  display: flex;
  flex: 1;
  & .button-row {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    @media ${theme?.breakpoints?.sm_up} {
      flex-direction: row;
      justify-content: flex-end;
    }
  }
`;

const Container = styled.div``;

export const TableGlobalStyles = `
  .MuiPaper-root {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
    border-radius: 8px !important;
    overflow: hidden;
  }

  .MuiTable-root {
    background-color: white;
  }

  thead tr {
    background-color: #f8f9fa !important;
    border-bottom: 2px solid #e1e3ee !important;
  }

  thead th {
    font-family: openSans-SemiBold, sans-serif !important;
    color: #7fa1c3 !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 14px 8px !important;
    white-space: nowrap;
    vertical-align: middle;
  }

  tbody tr {
    border-bottom: 1px solid #e1e3ee !important;
    transition: background-color 0.2s ease;
  }

  tbody tr:hover {
    background-color: #f8fafc !important;
  }

  tbody tr:last-child {
    border-bottom: none !important;
  }

  tbody td {
    font-family: openSans, sans-serif !important;
    padding: 12px 8px !important;
    font-size: 14px;
    vertical-align: middle;
    max-height: 60px;
    overflow: hidden;
  }

  @media (max-width: 1024px) {
    thead th {
      font-size: 11px !important;
      padding: 12px 6px !important;
    }

    tbody td {
      padding: 10px 6px !important;
      font-size: 13px;
    }
  }

  @media (max-width: 768px) {
    .MuiTable-root {
      font-size: 12px;
    }

    thead th {
      font-size: 10px !important;
      padding: 8px 4px !important;
      white-space: break-spaces;
    }

    tbody tr {
      display: block;
      margin-bottom: 12px;
      border: 1px solid #e1e3ee !important;
      border-radius: 8px;
      background: white;
    }

    tbody td {
      display: block;
      text-align: right !important;
      padding-left: 40% !important;
      padding: 8px 8px !important;
      font-size: 12px;
      position: relative;
      border-bottom: 1px solid #f0f0f0;

      &::before {
        content: attr(data-label);
        position: absolute;
        left: 8px;
        font-weight: 600;
        color: #7fa1c3;
        font-size: 10px;
        text-transform: uppercase;
      }
    }

    tbody tr td:last-child {
      border-bottom: none !important;
    }
  }
`;

const CaseIdLink = styled.a`
  color: #00838c;
  text-decoration: none;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 100%;

  &:hover {
    color: #00838c;
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const CaseTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const CaseTitleText = styled.span`
  font-weight: ${({ editFlag }) => (editFlag ? 700 : 600)};
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 14px;
  word-break: break-word;
  line-height: 1.4;
  max-width: 100%;

  ${({ editFlag }) =>
    editFlag &&
    css`
      border-bottom: 2px dotted #ff9800;
    `}

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const EditBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background-color: #fff3e0;
  color: #e65100;
  border: 1px solid #ffe0b2;
  text-transform: uppercase;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const PartyNameContainer = styled.div`
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFont};
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
  font-family: ${theme.fonts.primaryFontSemiBold};

  @media (max-width: 768px) {
    padding: 3px 8px;
    font-size: 10px;
  }
`;

const ClaimValueContainer = styled.div`
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

const ContractNumberContainer = styled.div`
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFont};
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const DateColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
  }
`;

const DateText = styled.div`
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFont};
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;
  white-space: nowrap;

  &:hover {
    color: #667eea;
  }

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const DeleteButton = styled.button`
  background-color: transparent;
  border: 1px solid #d91b1b;
  color: #d91b1b;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${theme.fonts.primaryFontSemiBold};

  &:hover {
    background-color: #e74c3c;
    color: white;
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    padding: 2px 6px;
    font-size: 10px;
  }
`;
